import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../../hooks/useCart';

interface ShopifyProduct {
  id: string;
  name: string;
  category: string;
  price: number;
  image: string;
  tag: string;
  variantId: string;
  availableForSale: boolean;
}

export default function FeaturedCollection() {
  const [isVisible, setIsVisible] = useState(false);
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  const sectionRef = useRef<HTMLDivElement>(null);
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch('https://hcflfbujecaqzvhbikhn.supabase.co/functions/v1/shopify-featured-collection');
        const data = await res.json();
        if (data.products) setProducts(data.products);
      } catch (err) {
        console.error('Failed to fetch products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleAddToCart = (product: ShopifyProduct) => {
    if (!product.variantId || !product.availableForSale) return;
    addItem({ id: product.id, variantId: product.variantId, name: product.name, price: product.price, image: product.image });
    setAddedIds((prev) => new Set(prev).add(product.variantId));
    setTimeout(() => {
      setAddedIds((prev) => { const next = new Set(prev); next.delete(product.variantId); return next; });
    }, 1500);
  };

  const handleCardClick = (productId: string) => {
    const numericId = productId.split('/').pop();
    navigate(`/product/${numericId}`);
  };

  return (
    <section id="collection" ref={sectionRef} className="relative w-full bg-black py-16 sm:py-24 md:py-32">
      <div className="w-[90%] max-w-[1400px] mx-auto">
        <div className={`flex flex-col sm:flex-row justify-between items-start sm:items-end gap-3 mb-10 sm:mb-16 md:mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white">Featured Drops</h2>
          <p className="text-sm font-light text-gray-500 tracking-wider">Motion Made It Happen</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-2 border-[#00d4ff] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6">
            {products.map((product, index) => (
              <div
                key={product.id}
                className={`group relative bg-[#1a1a1a] rounded-xl overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(0,212,255,0.4)] cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
                data-product-shop
                onClick={() => handleCardClick(product.id)}
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/10 z-10 group-hover:to-black/20 transition-all duration-500"></div>
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center bg-white rounded-full text-black text-xs font-semibold z-20">
                    {product.tag}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 z-20 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-400">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleAddToCart(product); }}
                      disabled={!product.availableForSale}
                      className={`w-full py-3 rounded-full text-xs font-bold tracking-widest uppercase transition-all duration-300 cursor-pointer whitespace-nowrap ${
                        addedIds.has(product.variantId) ? 'bg-green-400 text-black'
                        : product.availableForSale ? 'bg-[#00d4ff] text-black hover:bg-white'
                        : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      }`}
                      style={product.availableForSale ? { boxShadow: '0 0 20px rgba(0,212,255,0.5)' } : {}}
                    >
                      {addedIds.has(product.variantId) ? '✓ Added!' : product.availableForSale ? 'Add to Cart' : 'Sold Out'}
                    </button>
                  </div>
                </div>
                <div className="bg-black p-4 sm:p-6 flex items-center justify-between">
                  <div>
                    <h3 className="text-white text-base sm:text-lg font-bold mb-1">{product.name}</h3>
                    <p className="text-gray-400 text-xs sm:text-sm font-light">{product.category}</p>
                  </div>
                  {product.price > 0 && (
                    <p className="text-[#00d4ff] text-base sm:text-lg font-semibold">${product.price.toFixed(2)}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
